import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, LayoutGrid, Save, Upload, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ScenarioCard } from './ScenarioCard';
import { SaveScenarioDialog } from './SaveScenarioDialog';
import { LoadScenarioDialog } from './LoadScenarioDialog';
import { TemplateDialog } from './TemplateDialog';
import { SaveAsTemplateDialog } from './SaveAsTemplateDialog';
import { MarketConditionsChart } from './MarketConditionsChart';
import { TimelineVisualization } from './TimelineVisualization';
import { useSavedScenarios } from '@/hooks/useSavedScenarios';
import { usePersonalTemplates } from '@/hooks/usePersonalTemplates';
import { calculateScenarioAnalysis, compareScenarios, createScenarioFromFormData } from '@/utils/supreme-intelligence/scenarioComparison';
import { generateMarketComparisonData } from '@/utils/supreme-intelligence/marketConditions';
import { generateTimelineData } from '@/utils/supreme-intelligence/timelineProjections';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { ScenarioInput, ScenarioAnalysis } from '@/types/supremeIntelligenceTypes';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ScenarioComparisonProps {
  formData: EnhancedUnifiedFormData;
}

export const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({ formData }) => {
  const { saveScenarios, loadScenarios, savedScenarios } = useSavedScenarios();
  const { saveTemplate } = usePersonalTemplates();
  const [scenarios, setScenarios] = useState<ScenarioInput[]>(() => [
    createScenarioFromFormData(formData, 'Current Scenario'),
  ]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);

  // Track changes to scenarios
  React.useEffect(() => {
    setHasUnsavedChanges(true);
  }, [scenarios]);

  const analyses = useMemo(
    () => scenarios.map(scenario => calculateScenarioAnalysis(scenario, formData)),
    [scenarios, formData]
  );

  const metrics = useMemo(() => compareScenarios(analyses), [analyses]);

  const marketData = useMemo(() => generateMarketComparisonData(scenarios), [scenarios]);
  
  const timelineData = useMemo(() => scenarios.map(s => generateTimelineData(s, 3)), [scenarios]);

  const handleAddScenario = () => {
    if (scenarios.length >= 4) return;
    
    const newScenario = createScenarioFromFormData(formData, `Scenario ${scenarios.length + 1}`);
    setScenarios([...scenarios, newScenario]);
  };

  const handleUpdateScenario = (index: number, updatedScenario: ScenarioInput) => {
    const newScenarios = [...scenarios];
    newScenarios[index] = updatedScenario;
    setScenarios(newScenarios);
  };

  const handleRemoveScenario = (index: number) => {
    if (scenarios.length <= 2) return;
    setScenarios(scenarios.filter((_, i) => i !== index));
  };

  const handleSave = async (params: any) => {
    try {
      await saveScenarios(params);
      toast.success('Scenarios saved successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save scenarios');
    }
  };

  const handleLoad = async (id: string) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Load saved scenarios anyway?');
      if (!confirmed) return;
    }

    try {
      const result = await loadScenarios(id);
      if (result && result.scenarios) {
        setScenarios(result.scenarios);
        toast.success('Scenarios loaded successfully');
        setHasUnsavedChanges(false);
        setLoadDialogOpen(false);
      }
    } catch (error) {
      toast.error('Failed to load scenarios');
    }
  };

  const handleSelectTemplate = (templateScenarios: ScenarioInput[]) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Load template anyway?');
      if (!confirmed) return;
    }

    setScenarios(templateScenarios);
    toast.success('Template loaded successfully');
    setHasUnsavedChanges(false);
    setTemplateDialogOpen(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const recommendedScenario = analyses.reduce((best, current) =>
    current.affordabilityScore > best.affordabilityScore ? current : best
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <LayoutGrid className="w-6 h-6 text-primary" />
            <h3 className="text-2xl font-bold">Scenario Comparison</h3>
          </div>
          <p className="text-muted-foreground">
            Compare different mortgage scenarios side-by-side
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setTemplateDialogOpen(true)}
            className="gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Use Template
          </Button>
          <Button
            variant="outline"
            onClick={() => setSaveTemplateDialogOpen(true)}
            disabled={scenarios.length < 2}
            className="gap-2"
          >
            <Star className="w-4 h-4" />
            Save as Template
          </Button>
          <Button
            variant="outline"
            onClick={() => setLoadDialogOpen(true)}
            className="gap-2"
          >
            <Upload className="w-4 h-4" />
            Load Scenarios
          </Button>
          <Button
            variant="outline"
            onClick={() => setSaveDialogOpen(true)}
            className="gap-2"
          >
            <Save className="w-4 h-4" />
            Save Scenarios
          </Button>
          <Button
            onClick={handleAddScenario}
            disabled={scenarios.length >= 4}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Scenario
          </Button>
        </div>
      </div>

      {/* Scenario Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenarios.map((scenario, index) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            analysis={analyses[index]}
            metrics={metrics}
            onUpdate={(updated) => handleUpdateScenario(index, updated)}
            onRemove={() => handleRemoveScenario(index)}
            canRemove={scenarios.length > 2}
            index={index}
          />
        ))}
      </div>

      {/* Comparison Table */}
      {scenarios.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Detailed Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Metric</TableHead>
                    {scenarios.map((scenario) => (
                      <TableHead key={scenario.id} className="text-center">
                        {scenario.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Home Price</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {formatCurrency(analysis.input.homePrice)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Down Payment</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {formatCurrency(analysis.downPaymentAmount)} ({analysis.input.downPaymentPercentage}%)
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Loan Amount</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {formatCurrency(analysis.loanAmount)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Interest Rate</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {analysis.input.interestRate.toFixed(3)}%
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Loan Term</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {analysis.input.loanTerm} years
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-muted/50">
                    <TableCell className="font-medium">Monthly Payment</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center font-semibold">
                        {formatCurrency(analysis.monthlyPayment.total)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Total Interest</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {formatCurrency(analysis.totalInterest)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-muted/50">
                    <TableCell className="font-medium">Total Cost (30yr)</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center font-semibold">
                        {formatCurrency(analysis.totalCost30Year)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Affordability Score</TableCell>
                    {analyses.map((analysis) => (
                      <TableCell key={analysis.id} className="text-center">
                        {analysis.affordabilityScore}/100
                      </TableCell>
                    ))}
                  </TableRow>
                  {analyses.some(a => a.dtiRatio > 0) && (
                    <TableRow>
                      <TableCell className="font-medium">DTI Ratio</TableCell>
                      {analyses.map((analysis) => (
                        <TableCell key={analysis.id} className="text-center">
                          {analysis.dtiRatio > 0 ? `${analysis.dtiRatio.toFixed(1)}%` : 'N/A'}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Recommended Scenario</p>
                <p className="text-2xl font-bold">{recommendedScenario.input.name}</p>
                <p className="text-muted-foreground mt-1">
                  Affordability Score: <span className="font-semibold text-foreground">{recommendedScenario.affordabilityScore}/100</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialogs */}
      <SaveScenarioDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        scenarios={scenarios}
        formData={formData}
        onSave={handleSave}
      />

      <LoadScenarioDialog
        open={loadDialogOpen}
        onOpenChange={setLoadDialogOpen}
        scenarios={savedScenarios}
        onLoad={handleLoad}
      />

      <TemplateDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
};
